import React from 'react';
import { Modal, Form, Input, Select } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { usuariosApi } from '../../services/api';

const { Option } = Select;

const UsuarioFormModal = ({ 
    visible, 
    onCancel, 
    onSuccess, 
    editingUser = null, 
    roles = [], 
    campus = [] 
}) => {
    const [form] = Form.useForm();
    
    // Reset form when modal opens or closes
    React.useEffect(() => {
        if (visible) {
            form.resetFields();
            
            // Fill form with user data if editing
            if (editingUser) {
                form.setFieldsValue({
                    nombre: editingUser.nombre,
                    email: editingUser.email,
                    cedula: editingUser.cedula,
                    rol_id: editingUser.rol_id,
                    campus_id: editingUser.campus_id,
                    estado: editingUser.estado,
                });
            }
        }
    }, [visible, form, editingUser]);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            
            if (editingUser) {
                // Actualizar usuario
                const userData = {
                    name: values.nombre,
                    email: values.email,
                    cedula: values.cedula,
                    estado: values.estado,
                    rol_id: values.rol_id,
                    campus_id: values.campus_id
                };
                await usuariosApi.update(editingUser.id, userData);
                
                toast.success(`Usuario ${values.nombre} actualizado correctamente`, {
                    position: "top-right",
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            } else {
                // Crear nuevo usuario
                const userData = {
                    name: values.nombre,
                    email: values.email,
                    cedula: values.cedula,
                    password: values.password,
                    rol_id: values.rol_id,
                    campus_id: values.campus_id
                };
                await usuariosApi.register(userData);
                
                toast.success(`Usuario ${values.nombre} creado correctamente`, {
                    position: "top-right",
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            }
            
            onSuccess();
        } catch (error) {
            console.error("Error guardando usuario:", error);
            let errorMessage = "Error al guardar el usuario";
            
            if (error.response && error.response.data) {
                errorMessage = error.response.data.message || errorMessage;
            }
            
            toast.error(errorMessage, {
                position: "top-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
        }
    };

    const modalTitle = (
        <div className="flex items-center">
            <UserOutlined className="text-xl text-blue-500 mr-2" />
            <span>
                {editingUser 
                    ? `Editar Usuario - ${editingUser.nombre}`
                    : "Crear Nuevo Usuario"}
            </span>
        </div>
    );

    return (
        <Modal
            title={modalTitle}
            open={visible}
            onOk={handleOk}
            onCancel={onCancel}
            width={600}
            okText={editingUser ? "Actualizar" : "Crear"}
            cancelText="Cancelar"
            className="top-8"
            okButtonProps={{ className: "bg-blue-600 hover:bg-blue-700" }}
        >
            <Form
                form={form}
                layout="vertical"
                name="userForm"
                className="[&_.ant-form-item]:mb-4"
            >
                <Form.Item
                    name="nombre"
                    label="Nombre completo"
                    rules={[{ required: true, message: 'Por favor ingrese el nombre' }]}
                >
                    <Input placeholder="Nombre y apellidos" size="large" />
                </Form.Item>

                <Form.Item
                    name="email"
                    label="Email institucional"
                    rules={[
                        { required: true, message: 'Por favor ingrese el email' },
                        { type: 'email', message: 'Ingrese un email válido' }
                    ]}
                >
                    <Input placeholder="ejemplo@udi.edu.co" size="large" />
                </Form.Item>

                <Form.Item
                    name="cedula"
                    label="Cédula"
                    rules={[{ required: true, message: 'Por favor ingrese la cédula' }]}
                >
                    <Input placeholder="Número de cédula" size="large" />
                </Form.Item>

                {!editingUser && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                            name="password"
                            label="Contraseña"
                            rules={[{ required: true, message: 'Por favor ingrese la contraseña' }]}
                        >
                            <Input.Password placeholder="Contraseña" size="large" />
                        </Form.Item>

                        <Form.Item
                            name="password_confirmation"
                            label="Confirmar Contraseña"
                            dependencies={['password']}
                            rules={[
                                {
                                    required: true,
                                    message: 'Por favor confirme la contraseña',
                                },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('Las contraseñas no coinciden'));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password placeholder="Confirmar contraseña" size="large" />
                        </Form.Item>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Form.Item
                        name="rol_id"
                        label="Rol del usuario"
                        rules={[{ required: true, message: 'Por favor seleccione un rol' }]}
                    >
                        <Select placeholder="Seleccione un rol" size="large">
                            {roles.map(rol => (
                                <Option key={rol.id} value={rol.id}>{rol.rol}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="campus_id"
                        label="Campus"
                        rules={[{ required: true, message: 'Por favor seleccione un campus' }]}
                    >
                        <Select placeholder="Seleccione un campus" size="large">
                            {campus.map(c => (
                                <Option key={c.id} value={c.id}>{c.campus}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {editingUser && (
                        <Form.Item
                            name="estado"
                            label="Estado del usuario"
                            rules={[{ required: true, message: 'Por favor seleccione un estado' }]}
                        >
                            <Select placeholder="Seleccione un estado" size="large">
                                <Option value="activo">Activo</Option>
                                <Option value="inactivo">Inactivo</Option>
                            </Select>
                        </Form.Item>
                    )}
                </div>
            </Form>
        </Modal>
    );
};

export default UsuarioFormModal;